import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ThemeToggle } from '../../atoms/ThemeToggle/ThemeToggle'
import { useAuth } from '../../../hooks/useAuth'
import styles from './Header.module.css'

interface NavItem {
  label: string
  href: string
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Projects', href: '/#projects' },
  { label: 'Skills', href: '/#skills' },
  { label: 'GitHub', href: '/#github' },
  { label: 'Contact', href: '/contact' },
  { label: 'Blog', href: '/blog' },
]

export function Header() {
  const { token, user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const navRef = useRef<HTMLUListElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)
  const location = useLocation()
  const navigate = useNavigate()

  // Effet glassmorphism au scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fermeture du menu au clic extérieur
  useEffect(() => {
    if (!menuOpen) return

    const handleClick = (e: MouseEvent) => {
      if (
        navRef.current &&
        !navRef.current.contains(e.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  // Fermeture au resize > 600px
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 600) setMenuOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleNavClick = () => setMenuOpen(false)

  // Gestion des liens avec ancre (/#projects, /#skills, /#github)
  const handleHashNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    setMenuOpen(false)
    const hash = href.split('#')[1]
    if (location.pathname === '/') {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
    } else {
      navigate('/')
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
      }, 150)
    }
  }

  const isAdmin = user?.roles?.includes('Admin')

  return (
    <header
      className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}
      role="banner"
    >
      <div className={styles.inner}>
        {/* Logo */}
        <a href="/" className={styles.logo} aria-label="Steven YAMBOS — Accueil">
          <span className={styles.logoText}>SY.</span>
          <span className={styles.logoCursor} aria-hidden="true" />
        </a>

        {/* Navigation desktop */}
        <nav className={styles.nav} aria-label="Navigation principale">
          <ul
            ref={navRef}
            className={`${styles.navList} ${menuOpen ? styles.navListOpen : ''}`}
          >
            {NAV_ITEMS.map(({ label, href }) => {
              const isHashLink = href.includes('#')
              return (
                <li key={href} className={styles.navItem}>
                  {isHashLink ? (
                    <a
                      href={href}
                      className={styles.navLink}
                      onClick={(e) => handleHashNavClick(e, href)}
                    >
                      {label}
                    </a>
                  ) : (
                    <Link
                      to={href}
                      className={styles.navLink}
                      onClick={handleNavClick}
                    >
                      {label}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>

          {/* Controls */}
          <div className={styles.controls}>
            <ThemeToggle />

            {/* New article button for admin users */}
            {token && isAdmin && (
              <Link to="/create-article" className={styles.authButton}>
                + Nouvel article
              </Link>
            )}

            {/* Account button */}
            {token && (
              <Link to="/account" className={styles.authButton}>
                Mon compte
              </Link>
            )}

            {/* Hamburger */}
            <button
              ref={hamburgerRef}
              type="button"
              className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ''}`}
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
            >
              <span className={styles.bar} />
              <span className={styles.bar} />
              <span className={styles.bar} />
            </button>
          </div>
        </nav>
      </div>
    </header>
  )
}